import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    GetSessionError,
    useGetProxied,
} from '~/backend/connector-api/connector-components';
import {
    ImageDto,
    SessionPresentation,
} from '~/backend/connector-api/connector-schemas';
import { Modality } from '~/domain/dicom/modality';
import { EnvironmentObjectIds } from '~/engine/data/Environment';
import useRegistrationTask, {
    ConnectorContourPropagationTaskInput,
} from '~/hooks/workflows/connector/tasks/useContourPropagationTask';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import {
    PredictionModel,
    transformAvailableModelsData,
} from '~/utils/transforms/predictionModelsDataTransforms';
import ContourPropagation, {
    RegistrationTargetImage,
} from './ContourPropagation';

export function toRegistrationTargetImage(
    image: ImageDto
): RegistrationTargetImage {
    return {
        seriesId: image.series_instance_uid ?? '',
        studyId: image.study_instance_uid ?? '',
        modality: image.modality as Modality,
        label: image.display_name ?? undefined,
    };
}

type AdaptConnectorWorkflowProps = {
    sessionData: SessionPresentation | undefined;
    environmentObjectIds: EnvironmentObjectIds | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function AdaptConnectorWorkflow({
    sessionData,
    environmentObjectIds,
    isLoadingSession,
    error,
}: AdaptConnectorWorkflowProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { start: startProcessing, isSubmitting } = useRegistrationTask();
    const [selectedTargetImage, setSelectedTargetImage] = useState<
        RegistrationTargetImage | undefined
    >(undefined);

    const targetImages = useMemo(
        () =>
            sessionData?.payload?.images
                ? sessionData.payload.images.map(toRegistrationTargetImage)
                : [],
        [sessionData]
    );

    const [contourPropagationInput, setContourPropagationInput] =
        useState<ConnectorContourPropagationTaskInput>({
            sourceImageSeriesUid:
                environmentObjectIds?.primaryImage?.seriesInstanceUID ??
                sessionData?.payload?.context_image.series_instance_uid ??
                undefined,
            sourceImageStudyUid:
                environmentObjectIds?.primaryImage?.studyInstanceUID ??
                sessionData?.payload?.context_image.study_instance_uid ??
                undefined,
            targetImageSeriesUid: selectedTargetImage?.seriesId,
            targetImageStudyUid: selectedTargetImage?.studyId,
            targetImageModality: selectedTargetImage?.modality,
            sourceStructureSetUid:
                environmentObjectIds?.structureSet?.sopInstanceUID ??
                sessionData?.payload?.context_structure_set?.sop_instance_uid ??
                undefined,
            patientId:
                sessionData?.payload?.patient.id ?? selectedPatientId ?? '',
        });

    useEffect(() => {
        if (sessionData) {
            setSelectedTargetImage(
                sessionData?.payload?.images &&
                    sessionData.payload?.images.length == 1
                    ? toRegistrationTargetImage(sessionData.payload?.images[0])
                    : undefined
            );

            setContourPropagationInput((prev) => ({
                ...prev,
                sourceImageSeriesUid:
                    environmentObjectIds?.primaryImage?.seriesInstanceUID ??
                    sessionData.payload?.context_image?.series_instance_uid ??
                    undefined,
                sourceImageStudyUid:
                    environmentObjectIds?.primaryImage?.studyInstanceUID ??
                    sessionData.payload?.context_image?.study_instance_uid ??
                    undefined,
                sourceStructureSetUid:
                    environmentObjectIds?.structureSet?.sopInstanceUID ??
                    sessionData.payload?.context_structure_set
                        ?.sop_instance_uid ??
                    undefined,
                patientId: sessionData.payload?.patient.id ?? '',
            }));
        }
    }, [sessionData, environmentObjectIds]);

    useEffect(() => {
        if (selectedTargetImage) {
            setContourPropagationInput((prev) => ({
                ...prev,
                targetImageSeriesUid: selectedTargetImage.seriesId,
                targetImageStudyUid: selectedTargetImage.studyId,
                targetImageModality: selectedTargetImage.modality,
            }));
        }
    }, [selectedTargetImage]);

    const { data: modelsData, isLoading: isLoadingModels } = useGetProxied({
        pathParams: {
            rest: 'reg/list-active-models',
        },
    });

    const availableModels = modelsData
        ? transformAvailableModelsData(modelsData)
        : [];

    const onSubmit = (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => {
        startProcessing({
            ...contourPropagationInput,
            selectedModel,
            selectedStructures,
            selectedCustomization,
        });
    };

    return (
        <ContourPropagation
            availableModels={availableModels}
            onSubmit={onSubmit}
            isLoadingModels={isLoadingModels}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            targetImages={targetImages}
            selectedTargetImage={selectedTargetImage}
            setSelectedTargetImage={setSelectedTargetImage}
            error={error}
            showPseudonymization={false}
        />
    );
}
